import type { Pickup, StaticRouteGroup, UnassignedPickupState, UnassignedTabCounts } from "slices/pickup/UnassignedPickupState";

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Extracts tab counts from `/pickups/unassigned/grouped-by-static-route` response.
 * Supports multiple API shapes; extend here when backend adds fields.
 */
export function pickUnassignedTabCounts(raw: Record<string, unknown>): UnassignedTabCounts | undefined {
  const nested = raw.unassignedTabCounts ?? raw.tabCounts ?? raw.pickupTabCounts;
  if (nested && typeof nested === "object") {
    const o = nested as Record<string, unknown>;
    const all = o.all ?? o.totalAll ?? o.allCount;
    const atRisk = o.atRisk ?? o.at_risk ?? o.totalAtRisk ?? o.atRiskCount;
    const late = o.late ?? o.totalLate ?? o.lateCount;
    if (all !== undefined || atRisk !== undefined || late !== undefined) {
      return {
        all: num(all),
        atRisk: num(atRisk),
        late: num(late),
      };
    }
  }

  const all =
    raw.allPickupsCount ??
    raw.totalPickupsAll ??
    raw.totalUnassignedPickups ??
    raw.allCount;
  const atRisk =
    raw.atRiskPickupsCount ??
    raw.totalPickupsAtRisk ??
    raw.atRiskCount;
  const late = raw.latePickupsCount ?? raw.totalPickupsLate ?? raw.lateCount;

  if (all !== undefined || atRisk !== undefined || late !== undefined) {
    return {
      all: num(all),
      atRisk: num(atRisk),
      late: num(late),
    };
  }

  return undefined;
}

/** Maps API JSON to Redux slice shape; preserves tab counts when present. */
export function normalizeUnassignedGroupedApiResponse(
  raw: unknown,
  loadedForTerminalId?: number
): UnassignedPickupState {
  if (raw == null || typeof raw !== "object") {
    return {
      staticRouteGroups: [],
      pickupsWithoutStaticRoute: [],
      ...(loadedForTerminalId !== undefined ? { loadedForTerminalId } : {}),
    };
  }
  const r = raw as Record<string, unknown>;
  const groups = (r.staticRouteGroups ?? r.static_route_groups) as StaticRouteGroup[] | undefined;
  const loose = (r.pickupsWithoutStaticRoute ?? r.pickups_without_static_route) as Pickup[] | undefined;
  const tabCounts = pickUnassignedTabCounts(r);

  const next: UnassignedPickupState = {
    staticRouteGroups: Array.isArray(groups) ? groups : [],
    pickupsWithoutStaticRoute: Array.isArray(loose) ? loose : [],
  };
  if (tabCounts) {
    next.unassignedTabCounts = tabCounts;
  }
  if (loadedForTerminalId !== undefined) {
    next.loadedForTerminalId = loadedForTerminalId;
  }
  return next;
}
